import React, { useEffect, useRef, useState } from 'react';
import { array, bool, func, object, shape, string } from 'prop-types';
import { isEmpty } from 'lodash';

import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';

import SidebarPanel from './SidebarPanel';

import classes from './EditProfileForm.module.css';

const ButtonLabel = {
    [false]: 'Create',
    [true]:  'Creating'
};
const EditableTitle = {
    [true]:  'Create New Profile',
    [false]: 'Profile Details'
};

const EditProfileForm = ({
    editable,
    errors,
    onCancel,
    onChange,
    onSubmit,
    profile
}) => {
    const [initialProfile, setInitialProfile] = useState(profile);
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [nameValue, setNameValue] = useState(profile.name || '');

    // If the component mounted before a profile was available, re-initialize
    // the important state properties once a profile is available.
    useEffect(() => {
        if (!isEmpty(initialProfile) || isEmpty(profile)) {
            return;
        }
        setInitialProfile(profile);
        setNameValue(profile.name);
    }, [initialProfile, profile]);

    const deviceIds = profile.deviceIds || [];
    const hasDeviceIds = deviceIds.length > 0;
    const hasName = !!nameValue.trim();
    const nameErrors = Object.values(errors.name || {}).join(', ');
    const hasNameError = !!nameErrors.length;
    const isValid = hasDeviceIds && hasName;
    const isCancelable = !isSubmitting;
    const isSubmittable = !isSubmitting && isValid;

    function handleCancel(event) {
        setNameValue(initialProfile.name);
        onCancel();
    }

    function handleNameChange(event) {
        const name = event.target.value;
        setNameValue(name);
        onChange({
            ...profile,
            name
        });
    }

    async function handleSubmit(event) {
        event.preventDefault();
        if (!editable) {
            return;
        }
        setIsSubmitting(true);
        try {
            await onSubmit();
        // eslint-disable-next-line no-useless-catch
        } catch (error) {
            throw error;
        } finally {
            if (isMounted.current) {
                setIsSubmitting(false);
            }
        }
    }

    let footer = null;
    if (editable) {
        footer = (
            <footer className={classes.footer}>
                <Button
                    className={classes.button}
                    disabled={!isCancelable}
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
                <Button
                    className={classes.button}
                    color="primary"
                    disabled={!isSubmittable}
                    type="submit"
                    variant="contained"
                >
                    {ButtonLabel[isSubmitting]}
                </Button>
            </footer>
        );
    }

    return (
        <SidebarPanel title={EditableTitle[editable]}>
            <form className={classes.form} onSubmit={handleSubmit}>
                <TextField
                    autoComplete="off"
                    disabled={!editable}
                    error={hasNameError}
                    FormHelperTextProps={{
                        classes: {
                            root: classes.input
                        }
                    }}
                    helperText={nameErrors}
                    id="name"
                    InputLabelProps={{
                        classes: {
                            focused: classes.labelFocused,
                            root:    classes.label
                        }
                    }}
                    InputProps={{
                        classes: {
                            root: classes.input
                        }
                    }}
                    label="Profile Name"
                    onChange={handleNameChange}
                    value={nameValue}
                />
                {footer}
            </form>
        </SidebarPanel>
    );
};

EditProfileForm.defaultProps = {
    editable: true,
    errors:   {},
    onCancel: () => {},
    onChange: () => {},
    onSubmit: () => {},
    profile:  {}
};

EditProfileForm.propTypes = {
    editable: bool,
    errors:   object,
    onCancel: func,
    onChange: func,
    onSubmit: func,
    profile:  shape({
        devices: array,
        name:    string
    })
};

export default EditProfileForm;
